/**
 * TanStack Query hooks для текущего аккаунта.
 * Подключается к /api/v1/account/*.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/lib/api';
import { useAuthStore } from '@/store/authStore';

export type AccountRole = 'user' | 'superadmin';
export type AccountLocale = 'ua' | 'ru' | 'en';

export interface Account {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string | null;
  role: AccountRole;
  locale: AccountLocale;
  is_active: boolean;
  tariff_plan_id: string | null;
  created_at: string;
}

export interface AccountUpdate {
  full_name?: string;
  locale?: AccountLocale;
  avatar_url?: string | null;
}

export function useMe() {
  return useQuery<Account>({
    queryKey: ['account', 'me'],
    queryFn: async () => (await api.get<Account>('/account/me')).data,
  });
}

export function useMyBalance() {
  return useQuery<{ available_balance: string; reserved_balance: string }>({
    queryKey: ['account', 'balance'],
    queryFn: async () =>
      (await api.get<{ available_balance: string; reserved_balance: string }>(
        '/account/me/balance',
      )).data,
    refetchInterval: 30_000,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  const setAuth = useAuthStore((s) => s.setAuth);
  return useMutation({
    mutationFn: async (body: AccountUpdate) =>
      (await api.patch<Account>('/account/me', body)).data,
    onSuccess: (account) => {
      qc.setQueryData(['account', 'me'], account);
      // Синхронизируем store (locale / full_name)
      const token = useAuthStore.getState().accessToken;
      if (token) {
        setAuth(account, token);
      }
      qc.invalidateQueries({ queryKey: ['account', 'me'] });
    },
  });
}
